/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { usePageDesignerMode } from '@salesforce/storefront-next-runtime/design/react/core';
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators';
import { Link } from '@/components/link';
import { ResponsiveBannerImage } from '@/components/responsive-banner-image';
import { cn } from '@/lib/utils';
import type { Image } from '@/types';

type BannerStyle = 'hero' | 'card';

/**
 * DIS widths per style: the hero spans the page container at every breakpoint; a card is ~2/3 of
 * the viewport on mobile (1.5 per view) and a third of the row from md up.
 */
const BANNER_IMAGE_WIDTHS: Record<BannerStyle, string[]> = {
    hero: ['100vw'],
    card: ['70vw', '70vw', '33vw'],
};

@Component('banner', {
    name: 'Banner',
    group: 'priceshoes_content',
    description:
        'A single banner: background image (with optional mobile image), optional eyebrow, title, text and CTA button. Only the button navigates.',
})
@RegionDefinition([])
// oxlint-disable-next-line react/only-export-components -- oxlint flags the co-exported Page Designer metadata class; eslint-plugin-react-refresh does not
export class BannerMetadata {
    @AttributeDefinition({ name: 'Image', type: 'image', description: 'Background image (desktop)' })
    image!: string;

    @AttributeDefinition({
        name: 'Mobile Image',
        type: 'image',
        required: false,
        description: 'Background image for mobile. Falls back to the desktop image when empty.',
    })
    mobileImage?: string;

    @AttributeDefinition({ name: 'Image Alt Text', type: 'string', required: false })
    imageAlt?: string;

    @AttributeDefinition({
        name: 'Eyebrow',
        type: 'string',
        required: false,
        description: 'Short text above the title',
    })
    eyebrow?: string;

    @AttributeDefinition({ name: 'Title', type: 'string', required: false })
    title?: string;

    @AttributeDefinition({ name: 'Text', type: 'string', required: false })
    text?: string;

    @AttributeDefinition({ name: 'Button Text', type: 'string', required: false })
    ctaText?: string;

    @AttributeDefinition({
        name: 'Button Link',
        type: 'url',
        required: false,
        description: 'The button is shown only when both text and link are set',
    })
    ctaLink?: string;

    @AttributeDefinition({
        id: 'style',
        name: 'Style',
        description:
            'Default: hero (also when empty). hero: large home banner (380px on desktop, 2:1 on mobile, centered text, white button). card: campaign card (179px on desktop, 120px on mobile, text at the bottom, "Ver campaña >" link), e.g. Campañas.',
        type: 'enum',
        // Literal list: the cartridge generator reads decorators statically. Keep in sync with BannerStyle.
        values: ['hero', 'card'],
        defaultValue: 'hero',
    })
    style?: string;
}

interface BannerProps {
    image?: Image;
    mobileImage?: Image;
    imageAlt?: string;
    eyebrow?: string;
    title?: string;
    text?: string;
    ctaText?: string;
    ctaLink?: string;
    /** 'hero' (default) or 'card'. PD enums arrive as strings. */
    style?: string;
    /** Set by the Banner Carousel: only the first slide is the LCP candidate. */
    priority?: 'high' | 'auto';
    loading?: 'eager' | 'lazy';
    className?: string;
}

/**
 * Banner slide. The slide itself is not a link (CA6): navigation happens only through the CTA
 * button. Fixed height per breakpoint so slides never shift the page while images load (CLS).
 */
export default function Banner({
    image,
    mobileImage,
    imageAlt,
    eyebrow,
    title,
    text,
    ctaText,
    ctaLink,
    style,
    priority = 'auto',
    loading = 'lazy',
    className,
}: BannerProps) {
    const { t } = useTranslation('common');
    const { isDesignMode } = usePageDesignerMode();
    const showCta = Boolean(ctaText?.trim() && ctaLink);
    const isEmpty = !image?.url && !eyebrow?.trim() && !title?.trim() && !text?.trim() && !showCta;

    if (isEmpty && !isDesignMode) return null;

    const variant: BannerStyle = style === 'card' ? 'card' : 'hero';
    const background = (
        <ResponsiveBannerImage
            image={image}
            mobileImage={mobileImage}
            alt={imageAlt}
            widths={BANNER_IMAGE_WIDTHS[variant]}
            priority={priority}
            loading={loading}
            className="absolute inset-0 size-full"
        />
    );

    if (variant === 'card') {
        // content bottom-aligned, CTA as a text link. Mobile
        // follows the home mobile prototype (m-camp-banner): 120px tall with smaller type.
        return (
            <div
                data-slot="banner"
                data-style="card"
                className={cn(
                    'relative h-[120px] w-full overflow-hidden rounded-2xl bg-ps-gray-200 md:h-[179px]',
                    className
                )}>
                {background}
                <div className="relative flex h-full flex-col justify-end px-4 py-4 text-ps-white md:p-4">
                    {eyebrow?.trim() && (
                        <p className="text-[10px] font-extrabold uppercase leading-[14px] tracking-[0.27px] md:text-base md:font-medium md:leading-6">
                            {eyebrow}
                        </p>
                    )}
                    {title?.trim() ? (
                        <h3 className="mt-0.5 text-base font-bold leading-5 tracking-[0.2px] md:mt-0 md:text-2xl md:leading-[30px]">
                            {title}
                        </h3>
                    ) : (
                        isEmpty && (
                            <h3 className="text-base font-bold leading-5 md:text-2xl md:leading-[30px]">
                                {t('banner.emptyTitle')}
                            </h3>
                        )
                    )}
                    {text?.trim() && <p className="text-xs md:text-sm md:leading-[22px]">{text}</p>}
                    {showCta && (
                        // 24px tall on mobile: smallest touch target WCAG 2.5.8 allows.
                        <Link
                            to={ctaLink as string}
                            className="mt-0.5 inline-flex w-fit items-center gap-0.5 rounded-lg py-1 pr-2 text-[11px] font-semibold leading-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ps-white md:mt-1 md:gap-1 md:py-1.5 md:text-sm md:leading-[22px]">
                            {ctaText}
                            <ChevronRight className="size-3.5 md:size-5" aria-hidden="true" />
                        </Link>
                    )}
                </div>
            </div>
        );
    }

    // Mobile (Figma home mobile): 2:1 like the design, never shorter than 180px so a two-line title and
    // the button still fit on narrow phones. Desktop keeps the fixed 380px. Both are known before the
    // image loads, so there is no layout shift.
    return (
        <div
            data-slot="banner"
            className={cn(
                'relative aspect-[2/1] min-h-[180px] w-full overflow-hidden rounded-2xl bg-ps-gray-200 md:aspect-auto md:h-[380px] md:min-h-0 md:rounded-3xl',
                className
            )}>
            {background}

            {/* Text color is fixed by the design (Figma), not configurable. Bottom padding on mobile leaves room for the carousel dots. */}
            <div className="relative flex h-full flex-col justify-center gap-1 px-5 pb-8 pt-4 text-ps-white md:max-w-3xl md:gap-4 md:px-20 md:pb-12 md:pt-8">
                {eyebrow?.trim() && (
                    <p className="text-xs font-bold uppercase tracking-[0.27px] md:text-lg md:font-medium md:tracking-normal">
                        {eyebrow}
                    </p>
                )}
                {title?.trim() ? (
                    <h2 className="text-xl font-bold leading-tight md:text-[40px] md:font-semibold">{title}</h2>
                ) : (
                    isEmpty && (
                        <h2 className="text-xl font-bold md:text-[40px] md:font-semibold">{t('banner.emptyTitle')}</h2>
                    )
                )}
                {text?.trim() && <p className="text-sm font-medium md:text-2xl">{text}</p>}
                {showCta && (
                    <Link
                        to={ctaLink as string}
                        className="mt-2 inline-flex h-9 w-fit items-center justify-center rounded-md bg-ps-white px-5 text-sm font-medium text-ps-black hover:bg-ps-gray-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ps-focus md:mt-4 md:h-11 md:min-w-35 md:px-8 md:text-base">
                        {ctaText}
                    </Link>
                )}
            </div>
        </div>
    );
}
